/**
 * @fileoverview Review step of the WIP spreadsheet import (timesheet settings).
 *
 * Two halves, in this order:
 *  1. What the file proves — billing increment, date range, row integrity,
 *     totals. Stated with their evidence, not asked.
 *  2. What needs a decision — category, grade and staff matches.
 *
 * This screen is the safety mechanism of the import: it is the only place the
 * derived configuration is shown before it is written, so anything that would
 * make the commit write a wrong number blocks the button rather than warning
 * beside it. See `wip-import-review-checks.ts` for those rules.
 */

import { useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import { AlertTriangle, Check, Info } from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { WipAnalysis } from "@cvx/timesheets/import/wipAnalysis";
import { Button, Switch } from "@/ui/primitives";
import { Spinner } from "@/ui/primitives/spinner";
import { extractErrorMessage } from "@/utils/errors";
import { cn } from "@/utils/misc";
import { formatAudExact } from "../format";
import {
  collectAdvisories,
  collectBlockers,
  type MatchSelection,
} from "./wip-import-review-checks";
import { MATCH_NONE, WipImportReviewMatches } from "./WipImportReviewMatches";
import { WipImportReviewFacts } from "./WipImportReviewFacts";

/** The AI's name matches, already narrowed to real org ids by the action. */
type Proposals = {
  categoryMatches: Array<{
    code: string;
    categoryId: Id<"timeCategories"> | null;
  }>;
  staffMatches: Array<{ name: string; userId: Id<"users"> | null }>;
};

function count(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

export function WipImportReview({
  runId,
  orgId,
  onDone,
}: {
  runId: Id<"timesheetImportRuns">;
  orgId: Id<"organizations">;
  onDone: () => void;
}) {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const { data: run } = useQuery(
    convexQuery(api.timesheets.import.runs.getRun, { runId }),
  );
  const { data: categories } = useQuery(
    convexQuery(api.timesheets.categories.listByOrg, { orgId }),
  );
  const { data: grades } = useQuery(
    convexQuery(api.timesheets.grades.listByOrg, { orgId, today }),
  );
  const { data: staff } = useQuery(
    convexQuery(api.timesheets.profiles.listOrgStaff, { orgId }),
  );
  const commitConfig = useMutation(api.timesheets.import.runs.commitConfig);

  const [categoryChoice, setCategoryChoice] = useState<Record<string, string>>(
    {},
  );
  const [categoryName, setCategoryName] = useState<Record<string, string>>({});
  const [gradeChoice, setGradeChoice] = useState<Record<string, string>>({});
  const [staffChoice, setStaffChoice] = useState<Record<string, string>>({});
  // Null until the user decides, so the default can depend on the analysis
  // (which is not available when this state is initialised).
  const [importSubCodes, setImportSubCodes] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);

  const analysis: WipAnalysis | undefined = run?.analysis;
  const proposals: Proposals | undefined = run?.proposals;

  // The existing categories, grades and staff are what the match dropdowns
  // resolve against. Rendering before they arrive shows every file record as
  // unmatched with an empty dropdown, and Apply would then create duplicates of
  // records the firm already has — the exact outcome this screen prevents.
  if (
    run === undefined ||
    categories === undefined ||
    grades === undefined ||
    staff === undefined
  ) {
    return (
      <StatusPanel>
        <Spinner className="size-4" />
        Loading this import…
      </StatusPanel>
    );
  }
  if (run === null) {
    return (
      <StatusPanel tone="error">We could not find that import.</StatusPanel>
    );
  }
  if (run.status === "failed") {
    return (
      <StatusPanel tone="error">
        {run.error ?? "That import failed."}
      </StatusPanel>
    );
  }
  if (run.status === "analysing") {
    return (
      <StatusPanel>
        <Spinner className="size-4" />
        Reading the spreadsheet and matching it to your settings…
      </StatusPanel>
    );
  }
  if (run.status === "config_committed") {
    return (
      <div className="flex flex-col gap-4">
        <StatusPanel>
          <Check className="size-4 text-emerald-600 dark:text-emerald-400" />
          These settings have already been applied.
        </StatusPanel>
        <div className="flex justify-end">
          <Button variant="outline" onClick={onDone}>
            Close
          </Button>
        </div>
      </div>
    );
  }
  if (!analysis) {
    return (
      <StatusPanel tone="error">
        This import has no analysis to review.
      </StatusPanel>
    );
  }

  // Morgan's proposal seeds each picker; an explicit user choice always wins.
  const categoryValue = (code: string) =>
    categoryChoice[code] ??
    proposals?.categoryMatches.find((m) => m.code === code)?.categoryId ??
    MATCH_NONE;
  const staffValue = (name: string) =>
    staffChoice[name] ??
    proposals?.staffMatches.find((m) => m.name === name)?.userId ??
    MATCH_NONE;
  // Grades are matched here rather than by the model: a grade match is an exact
  // name match or nothing, which needs no judgement.
  const gradeValue = (name: string) =>
    gradeChoice[name] ??
    grades?.find((g) => g.name.toLowerCase() === name.toLowerCase())?._id ??
    MATCH_NONE;

  const selected = (value: string): string | null =>
    value === MATCH_NONE ? null : value;
  const gradeSelections: MatchSelection[] = analysis.grades.map((g) => ({
    label: g.name,
    selectedId: selected(gradeValue(g.name)),
  }));
  const staffSelections: MatchSelection[] = analysis.staff.map((s) => ({
    label: s.name,
    selectedId: selected(staffValue(s.name)),
  }));

  const blockers = collectBlockers(analysis, {
    grades: gradeSelections,
    staff: staffSelections,
  });
  const advisories = collectAdvisories(analysis);

  const unitMinutes = analysis.unit.minutes;
  const rateEffectiveFrom = analysis.dateRange?.earliest;

  // The file's analysis codes carry a sub-code suffix (ADMIN03 = ADMIN, 03).
  // The full code is what the firm recognises and what an export has to
  // reproduce, so that is what gets stored.
  const subCodesFor = (code: string, subCodes: string[]) =>
    subCodes.map((sub) => `${code}${sub}`);
  const subCodeTotal = analysis.categories.reduce(
    (sum, c) => sum + c.subCodes.length,
    0,
  );
  const wantsSubCodes = importSubCodes ?? subCodeTotal > 0;

  const handleCommit = async () => {
    // Re-checked rather than defaulted. A guessed increment or a rate dated
    // "today" would be written silently and only surface much later, in a
    // remuneration report.
    if (blockers.length > 0 || unitMinutes === null || !rateEffectiveFrom) {
      return;
    }
    setBusy(true);
    try {
      const result = await commitConfig({
        runId,
        unitMinutes,
        rateEffectiveFrom,
        categories: analysis.categories.map((c) => {
          const chosen = categoryValue(c.code);
          return {
            code: c.code,
            categoryId:
              chosen === MATCH_NONE ? null : (chosen as Id<"timeCategories">),
            // Blank falls back to the code rather than blocking: the field is
            // optional polish, and an empty category name helps nobody.
            newName:
              chosen === MATCH_NONE
                ? (categoryName[c.code] ?? "").trim() || c.code
                : null,
            subCodes: wantsSubCodes ? subCodesFor(c.code, c.subCodes) : [],
          };
        }),
        grades: analysis.grades.map((g) => {
          const chosen = gradeValue(g.name);
          return {
            name: g.name,
            gradeId:
              chosen === MATCH_NONE ? null : (chosen as Id<"staffGrades">),
            hourlyRate: g.hourlyRate,
          };
        }),
        staffAssignments: analysis.staff.flatMap((s) => {
          const chosen = staffValue(s.name);
          if (chosen === MATCH_NONE) return [];
          return [{ userId: chosen as Id<"users">, gradeName: s.gradeName }];
        }),
      });
      toast.success(
        `Applied a ${unitMinutes}-minute increment, ` +
          `${count(result.categoriesCreated, "new category", "new categories")}, ` +
          (result.subCodesCreated > 0
            ? `${count(result.subCodesCreated, "sub-code", "sub-codes")}, `
            : "") +
          `${count(result.gradesCreated, "new grade", "new grades")}, ` +
          `${count(result.ratesCreated, "rate", "rates")} and ` +
          `${count(result.staffAssigned, "staff assignment", "staff assignments")}.` +
          // Someone matched on screen but left without a grade needs saying:
          // the assignment count alone reads as if they were simply not matched.
          (result.staffSkipped > 0
            ? ` ${count(result.staffSkipped, "person", "people")} could not be ` +
              "assigned a grade, because no rate was readable for it."
            : ""),
      );
      onDone();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not apply these settings."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <WipImportReviewFacts
        analysis={analysis}
        fileName={run.fileName}
        sheetName={run.sheetName}
      />

      {blockers.length > 0 && (
        <NoticePanel
          tone="blocking"
          title="These need sorting out before anything can be applied"
          items={blockers}
        />
      )}
      {advisories.length > 0 && (
        <NoticePanel tone="advisory" title="Worth knowing" items={advisories} />
      )}

      {subCodeTotal > 0 && (
        <div className="flex items-start justify-between gap-4 rounded-xl border border-border/60 bg-background p-4">
          <div>
            <p className="text-sm font-medium text-primary">Import sub-codes</p>
            <p className="mt-1 text-sm text-primary/60">
              {count(subCodeTotal, "sub-code", "sub-codes")} sit under the head
              categories in this file. Bringing them across lets your team pick
              the same codes when recording time. They stay optional on every
              entry, and you can edit the list afterwards.
            </p>
          </div>
          <Switch
            checked={wantsSubCodes}
            onCheckedChange={setImportSubCodes}
            disabled={busy}
            aria-label="Import sub-codes"
          />
        </div>
      )}

      <WipImportReviewMatches
        title="Time categories"
        description="Matched against your existing time categories. Anything left unmatched is created as a new category — the file carries only codes, so give it the name your team should see."
        disabled={busy}
        rows={analysis.categories.map((c) => ({
          key: c.code,
          label: c.code,
          detail:
            `${c.rows} rows` +
            (wantsSubCodes && c.subCodes.length > 0
              ? ` · ${subCodesFor(c.code, c.subCodes).join(", ")}`
              : ""),
          value: categoryValue(c.code),
          options: (categories ?? []).map((existing) => ({
            value: existing._id,
            label: existing.name,
          })),
          onChange: (value) =>
            setCategoryChoice((prev) => ({ ...prev, [c.code]: value })),
          unmatchedLabel: `Create "${c.code}"`,
          unmatchedBadge: { text: "New", color: "amber" },
          newName: {
            value: categoryName[c.code] ?? "",
            placeholder: c.code,
            onChange: (value) =>
              setCategoryName((prev) => ({ ...prev, [c.code]: value })),
          },
        }))}
      />

      <WipImportReviewMatches
        title="Staff grades and rates"
        description={
          "Rates come from the file" +
          (rateEffectiveFrom ? `, dated from ${rateEffectiveFrom}` : "") +
          ". Matching an existing grade adds or updates that grade's rate on that date; it does not rename the grade."
        }
        disabled={busy}
        rows={analysis.grades.map((g) => ({
          key: g.name,
          label: g.name,
          detail: `${formatAudExact(g.hourlyRate)}/hr · ${g.rows} rows`,
          value: gradeValue(g.name),
          options: (grades ?? []).map((existing) => ({
            value: existing._id,
            label:
              existing.currentRate !== undefined
                ? `${existing.name} — currently ${formatAudExact(existing.currentRate)}/hr`
                : existing.name,
          })),
          onChange: (value) =>
            setGradeChoice((prev) => ({ ...prev, [g.name]: value })),
          unmatchedLabel: `Create "${g.name}"`,
          unmatchedBadge: { text: "New", color: "amber" },
        }))}
      />

      <WipImportReviewMatches
        title="Staff"
        description="Matched to your organisation's members. A match replaces that member's current grade assignment. Anyone left unmatched is skipped here and handled when their time entries are imported."
        disabled={busy}
        rows={analysis.staff.map((s) => ({
          key: s.name,
          label: s.name,
          detail: `${s.gradeName || "no grade in file"} · ${s.rows} rows`,
          value: staffValue(s.name),
          options: (staff ?? []).map((member) => ({
            value: member.userId,
            label: `${member.name} (${member.staffCode})`,
          })),
          onChange: (value) =>
            setStaffChoice((prev) => ({ ...prev, [s.name]: value })),
          unmatchedLabel: "Not a member — skip",
          unmatchedBadge: { text: "Skipped", color: "gray" },
        }))}
      />

      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" onClick={onDone} disabled={busy}>
          Cancel
        </Button>
        <Button
          onClick={() => void handleCommit()}
          disabled={busy || blockers.length > 0}
        >
          {busy && <Spinner className="mr-1.5" />}
          Apply these settings
        </Button>
      </div>
    </div>
  );
}

function StatusPanel({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: "neutral" | "error";
}) {
  return (
    <p
      className={cn(
        "flex items-center gap-2 rounded-xl border border-border/60 bg-background px-5 py-4 text-sm",
        tone === "error" ? "text-destructive" : "text-muted-foreground",
      )}
    >
      {children}
    </p>
  );
}

function NoticePanel({
  tone,
  title,
  items,
}: {
  tone: "blocking" | "advisory";
  title: string;
  items: string[];
}) {
  const blocking = tone === "blocking";
  const Icon = blocking ? AlertTriangle : Info;
  return (
    <section
      className={cn(
        "rounded-xl border p-5",
        blocking
          ? "border-destructive/40 bg-destructive/5"
          : "border-amber-200/60 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-950/30",
      )}
    >
      <h3
        className={cn(
          "flex items-center gap-2 text-sm font-semibold",
          blocking ? "text-destructive" : "text-amber-900 dark:text-amber-200",
        )}
      >
        <Icon className="size-4 shrink-0" />
        {title}
      </h3>
      <ul
        className={cn(
          "mt-2 list-disc space-y-1 pl-5 text-sm",
          blocking ? "text-destructive" : "text-amber-900 dark:text-amber-200",
        )}
      >
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </section>
  );
}
